import {
  DataProvider,
  Filtering,
  QDataTable,
  QDataTableActionBar,
  QText,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';
import { AxiosError } from 'axios';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryClient } from 'react-query';
import { documentApi } from '../../../../../../api/document';
import { QualioDocument } from '../../../../../../api/model/document';
import { BulkActionsWrapper } from '../../BulkActionsV2/BulkActionWrapper';
import { getTableParams, TableFilter } from '../../Filter/TableFilters';
import { useDeleteDraftSelected } from '../../hooks/useDeleteDraftSelected';
import { useWorkspaceTableColumns } from '../../hooks/useWorkspaceTableColumns';
import { WorkspaceTabsContext } from '../../WorkspaceTabsContext';
import { WorkspaceTabs } from '../../WorkspaceV2';

const buildQueryKey = (companyId?: number) => [
  'your-documents',
  Number(companyId),
];

export const invalidateYourDocumentsTableData = async (
  queryClient: QueryClient,
  companyId: number,
) => {
  await queryClient.invalidateQueries(buildQueryKey(companyId));
};

export const YourDocumentsTable = () => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const currentUser = useCurrentUser();
  const deletedDraftStatusSelected = useDeleteDraftSelected();
  const { setIsReadyForExport } = useContext(WorkspaceTabsContext);
  const getUserDocuments = async ({
    pageParam,
  }: {
    pageParam: DataProvider.PageParams;
  }): Promise<DataProvider.TDataWithCount<QualioDocument>> => {
    setIsReadyForExport(false);
    const {
      offset,
      limit,
      orderBy,
      searchTerm,
      statusFilters,
      templateFilter,
      tagsFilter,
      ownerFilter,
    } = getTableParams(pageParam);
    let itemCount: number;
    let data: QualioDocument[] | undefined;
    try {
      ({ total: itemCount, documents: data } = await documentApi.fetchUserDocs(
        currentUser,
        statusFilters,
        searchTerm,
        tagsFilter,
        offset * limit,
        limit,
        orderBy,
        templateFilter,
        ownerFilter,
      ));
    } catch (error) {
      const slug = (error as AxiosError<{ slug?: string }>)?.response?.data
        ?.slug;
      if (slug === 'search_query_too_long') {
        showToast({
          'data-cy': 'your-documents-fetch-error-toast',
          id: 'your-documents-fetch-error',
          status: 'error',
          title: t('statuses.error'),
          description: t('apiErrors.search_query_too_long'),
          replace: true,
        });
      }
      throw error;
    }

    if (itemCount > 0 && data?.length > 0) {
      setIsReadyForExport(true);
    }

    return {
      itemCount,
      data: data || [],
    };
  };

  const columns = useWorkspaceTableColumns();

  return (
    <DataProvider.Remote
      queryKey={buildQueryKey(currentUser.companyId)}
      queryFn={getUserDocuments}
      queryParamKeys={{
        searchTerm: 'query',
        pageIndex: 'offset',
        pageSize: 'limit',
      }}
      queryOptions={{
        retry: (count, error) =>
          (error as AxiosError)?.response?.status !== 400 && count < 3,
      }}
    >
      {deletedDraftStatusSelected && (
        <QText fontSize="sm">
          Deleted items will remain in this view for 90 days
        </QText>
      )}
      <QDataTable
        data-cy="workspace-your-documents-table"
        columns={columns}
        getRowId={(row) => row.id.toString()}
      >
        <Filtering.FormContent>
          <TableFilter tab={WorkspaceTabs.YOUR_DOCUMENTS} />
        </Filtering.FormContent>
        {!deletedDraftStatusSelected && (
          <QDataTableActionBar>
            <BulkActionsWrapper currentTab={WorkspaceTabs.YOUR_DOCUMENTS} />
          </QDataTableActionBar>
        )}
      </QDataTable>
    </DataProvider.Remote>
  );
};
